import { deleteDB, openDB } from 'idb';
import * as Sentry from '@sentry/react';
import { approxJsonSize, dataUrlToBlob, mimeTypeFromDataUrl, reportIdbWriteSize, } from '../helpers/storageTelemetry';
let gameDB = null;
const GAME_DB_KEY = 'current';
const GAME_DB_VERSION = 2;
const openGameDB = () => openDB('gameDB', GAME_DB_VERSION, {
    async upgrade(db, oldVersion, _newVersion, transaction) {
        if (oldVersion < 1) {
            if (!db.objectStoreNames.contains('game')) {
                db.createObjectStore('game');
            }
            if (!db.objectStoreNames.contains('gameTasks')) {
                db.createObjectStore('gameTasks');
            }
            if (!db.objectStoreNames.contains('rallyInfo')) {
                db.createObjectStore('rallyInfo');
            }
        }
        if (oldVersion < 2) {
            if (!db.objectStoreNames.contains('uploads')) {
                db.createObjectStore('uploads', { keyPath: 'taskId' });
            }
            // Migrate any in-flight base64 uploads from inside the game blob
            // into the dedicated store. All work below stays on the version-change
            // transaction — operations are IDB ops or synchronous, never an
            // unrelated async hop that would let the transaction auto-commit.
            try {
                const gameStore = transaction.objectStore('game');
                const uploadsStore = transaction.objectStore('uploads');
                const legacy = (await gameStore.get(GAME_DB_KEY));
                if (legacy) {
                    if (Array.isArray(legacy.uploads) && legacy.uploads.length > 0) {
                        for (const item of legacy.uploads) {
                            if (!item?.mediaReference || typeof item.taskId !== 'number') {
                                continue;
                            }
                            try {
                                const mediaBlob = dataUrlToBlob(item.mediaReference);
                                const mimeType = mimeTypeFromDataUrl(item.mediaReference);
                                await uploadsStore.put({ taskId: item.taskId, mediaBlob, mimeType });
                            }
                            catch {
                                // Skip individual records that fail to decode rather than abort the whole migration.
                            }
                        }
                    }
                    if ('uploads' in legacy) {
                        delete legacy.uploads;
                        await gameStore.put(legacy, GAME_DB_KEY);
                    }
                }
            }
            catch (err) {
                // If migration fails, fall through — empty new store is still a valid v2 state.
                Sentry.captureException(err, {
                    tags: { db: 'gameDB', phase: 'upgrade', fromVersion: oldVersion },
                });
            }
        }
    },
    blocked(currentVersion, blockedVersion) {
        // Another tab/PWA instance is holding the previous DB connection open,
        // so our upgrade is queued. Surface it so we can see real-world incidence.
        Sentry.captureMessage(`gameDB upgrade blocked (${currentVersion} -> ${blockedVersion})`, 'warning');
    },
    blocking() {
        // A newer version wants to upgrade and we are blocking it. Drop our
        // connection so the other tab/PWA can proceed without forcing a reload.
        try {
            gameDB?.close();
        }
        finally {
            gameDB = null;
        }
    },
    terminated() {
        // Connection died (storage cleared, OS evicted, etc.). Force a fresh open
        // on next access rather than reusing a dead handle.
        gameDB = null;
    },
});
const initGameDB = async () => {
    if (gameDB) {
        return gameDB;
    }
    try {
        gameDB = await openGameDB();
    }
    catch (err) {
        const name = err?.name;
        // VersionError = local IDB is at a HIGHER version than this bundle expects.
        // Happens on iOS PWAs that previously loaded a newer bundle (which bumped
        // the schema) and have now reverted to a cached older bundle. We can't
        // downgrade, so blow the DB away and recreate at the current version.
        if (name === 'VersionError') {
            Sentry.captureException(err, {
                tags: {
                    db: 'gameDB',
                    phase: 'open',
                    cause: 'stale-bundle-downgrade',
                },
            });
            try {
                await deleteDB('gameDB');
                gameDB = await openGameDB();
            }
            catch (recoveryErr) {
                Sentry.captureException(recoveryErr, {
                    tags: { db: 'gameDB', phase: 'recover' },
                });
                throw recoveryErr;
            }
            return gameDB;
        }
        Sentry.captureException(err, { tags: { db: 'gameDB', phase: 'open' } });
        throw err;
    }
    return gameDB;
};
const putGameTelemetered = async (db, game) => {
    reportIdbWriteSize('game', GAME_DB_KEY, approxJsonSize(game));
    await db.put('game', game, GAME_DB_KEY);
};
export const GAME_DB = {
    SET: {
        async game(game) {
            const db = await initGameDB();
            await putGameTelemetered(db, game);
        },
        async gameTasks(gameTasks) {
            const db = await initGameDB();
            await db.put('gameTasks', gameTasks, GAME_DB_KEY);
        },
        async rallyInfo(rallyInfo) {
            const db = await initGameDB();
            await db.put('rallyInfo', rallyInfo, GAME_DB_KEY);
        },
        async gameTimeLeft(timeLeft) {
            const db = await initGameDB();
            await db.put('gameTimeLeft', timeLeft, GAME_DB_KEY);
        },
        async gameStatus(status) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.gameStatus = status;
            await putGameTelemetered(db, game);
        },
    },
    GET: {
        async game() {
            const db = await initGameDB();
            return (await db.get('game', GAME_DB_KEY)) ?? null;
        },
        async gameTasks() {
            const db = await initGameDB();
            return (await db.get('gameTasks', GAME_DB_KEY)) ?? null;
        },
        async rallyInfo() {
            const db = await initGameDB();
            return (await db.get('rallyInfo', GAME_DB_KEY)) ?? null;
        },
        async localAnswersTimeLogsList() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return null;
            }
            return game.localAnswersTimeLogs;
        },
        async gameStatus() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return null;
            }
            return game.gameStatus;
        },
        async localTaskAnswers() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return null;
            }
            return game.localTaskAnswers;
        },
        async taskUploadBlob(taskId) {
            const db = await initGameDB();
            const record = await db.get('uploads', taskId);
            if (!record) {
                return null;
            }
            return { blob: record.mediaBlob, mimeType: record.mimeType };
        },
    },
    UPDATE: {
        async gameState(updatedFields) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            await putGameTelemetered(db, {
                ...game,
                ...updatedFields,
            });
        },
        async taskResult(data) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            const { taskId: uid, status, pointsEarned, newAnswer } = data;
            const index = game.taskResults.findIndex((task) => task.taskId === uid);
            if (index === -1) {
                return;
            }
            game.taskResults[index] = {
                ...game.taskResults[index],
                status,
                totalPoints: game.taskResults[index].totalPoints + pointsEarned,
                playerAnswers: [...game.taskResults[index].playerAnswers, newAnswer],
            };
            game.totalPoints += pointsEarned;
            await putGameTelemetered(db, game);
        },
        async taskStatus(data) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            const { uid, status } = data;
            const index = game.taskResults.findIndex((task) => task.taskId === uid);
            if (index === -1) {
                return;
            }
            game.taskResults[index].status = status;
            await putGameTelemetered(db, game);
        },
        async addTaskAnswer(data) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            const { uid, newAnswer } = data;
            const index = game.taskResults.findIndex((task) => task.taskId === uid);
            if (index === -1) {
                return;
            }
            game.taskResults[index] = {
                ...game.taskResults[index],
                playerAnswers: [...game.taskResults[index].playerAnswers, newAnswer],
            };
            await putGameTelemetered(db, game);
        },
        async pushLocalAnswer(answer) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.localTaskAnswers.push(answer);
            await putGameTelemetered(db, game);
        },
        async clearLocalAnswers() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.localTaskAnswers = [];
            await putGameTelemetered(db, game);
        },
        async clearLocalAnswersByTaskIds(taskIds) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            const taskIdSet = new Set(taskIds);
            game.localTaskAnswers = game.localTaskAnswers.filter((answer) => !taskIdSet.has(answer.task));
            await putGameTelemetered(db, game);
        },
        async pushAnswerTimeLogs(currentTaskLogs) {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.localAnswersTimeLogs.push(currentTaskLogs);
            await putGameTelemetered(db, game);
        },
        async clearLocalAnswersTimeLogs() {
            const db = await initGameDB();
            const game = await db.get('game', GAME_DB_KEY);
            if (!game) {
                return;
            }
            game.localAnswersTimeLogs = [];
            await putGameTelemetered(db, game);
        },
        async pushTaskUploadBlob(item) {
            const db = await initGameDB();
            try {
                await db.put('uploads', {
                    taskId: item.taskId,
                    mediaBlob: item.blob,
                    mimeType: item.mimeType,
                });
            }
            catch {
                // Best-effort: the answer is also tracked in other code paths, so we
                // don't want to abort the submit if this telemetry-adjacent write fails.
            }
        },
    },
    RESET: {
        async game() {
            const db = await initGameDB();
            await db.clear('rallyInfo');
            await db.clear('game');
            await db.clear('gameTasks');
            await db.clear('uploads');
        },
    },
};
